import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { useRBAC } from '@/lib/rbac/context'
import { PersonPickerBar } from '@/components/performance/bonificacao/PersonPickerBar'
import { ProaBanner } from '@/components/performance/bonificacao/ProaBanner'
import { CollaboratorFilters } from '@/components/performance/bonificacao/CollaboratorFilters'
import { CollaboratorCard } from '@/components/performance/bonificacao/CollaboratorCard'
import { CycleSetupSheet } from '@/components/performance/bonificacao/CycleSetupSheet'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import { useBonusDashboard, useProaPercent } from '@/api/bonificacao-hooks'
import { filterAssessments, sortByStatus } from '@/components/performance/bonificacao/collaborator-utils'
import { MetricCard } from '@/components/MetricCard'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Users,
  FileEdit,
  ClipboardCheck,
  ShieldCheck,
  CheckCircle,
  TrendingUp,
  Trophy,
  Settings,
  UserPlus,

} from 'lucide-react'

interface BonificacaoSearch {
  employee?: string
}

function BonificacaoPage() {
  const { employee } = Route.useSearch()
  const setSelectedEmployee = useBonificacaoStore((s) => s.setSelectedEmployee)
  const selectedYear = useBonificacaoStore((s) => s.selectedYear)
  const setYear = useBonificacaoStore((s) => s.setYear)
  const filterDepartment = useBonificacaoStore((s) => s.filterDepartment)
  const filterStatus = useBonificacaoStore((s) => s.filterStatus)
  const filterSearch = useBonificacaoStore((s) => s.filterSearch)
  const setFilters = useBonificacaoStore((s) => s.setFilters)
  const { data, isLoading } = useBonusDashboard(selectedYear)
  const { data: proaData, isLoading: proaLoading } = useProaPercent(selectedYear)
  const { user } = useRBAC()
  const [cycleSheetOpen, setCycleSheetOpen] = useState(false)
  const [cycleSheetTab, setCycleSheetTab] = useState<'new' | 'edit'>('new')
  const isAdmin = user?.role === 'admin' || user?.role === 'rh'
  useEffect(() => {
    if (employee) {
      setSelectedEmployee(employee)
    }
  }, [])

  const stats = data?.stats
  const allAssessments = data?.assessments ?? []
  const isEmpty = !isLoading && !stats?.total

  // Director self-filter: directors only see their own card
  const isDirector = user?.role === 'diretor-tecnico'
  const currentEmployeeId = user?.employee?.employeeId
  const visibleAssessments = useMemo(() => {
    if (isDirector && currentEmployeeId) {
      return allAssessments.filter((a) => a.employeeId === currentEmployeeId)
    }
    return allAssessments
  }, [allAssessments, isDirector, currentEmployeeId])

  // Apply user filters + sorting
  const filteredAssessments = useMemo(() => {
    const filtered = filterAssessments(visibleAssessments, {
      department: filterDepartment,
      status: filterStatus,
      search: filterSearch,
    })
    return sortByStatus(filtered)
  }, [visibleAssessments, filterDepartment, filterStatus, filterSearch])

  const showFilters = !isEmpty && !isDirector

  return (
    <div>
      <PersonPickerBar />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-display font-bold text-white">Bonificação</h1>
          <div className="flex items-center gap-1 bg-brand-dark-lighter rounded-monumenta p-1" data-testid="year-selector">
            {[2025, 2026].map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYear(y)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedYear === y
                    ? 'bg-brand-primary text-brand-dark'
                    : 'text-brand-gray hover:text-white hover:bg-brand-dark-light'
                }`}
                data-testid={`year-btn-${y}`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        <ProaBanner
          meta={proaData?.percent != null ? 100 : 0}
          atingido={proaData?.percent ?? 0}
          passed={proaData?.passed ?? false}
          isLoading={proaLoading}
          noData={!proaLoading && !proaData}
        />

        {/* Cycle management buttons — visible when cycle exists and user is admin/RH */}
        {!isEmpty && isAdmin && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-monumenta"
              onClick={() => { setCycleSheetTab('edit'); setCycleSheetOpen(true) }}
              data-testid="edit-cycle-btn"
            >
              <Settings className="h-4 w-4" /> Editar Ciclo
            </button>
            <button
              type="button"
              className="btn-ghost text-sm flex items-center gap-1.5 px-3 py-1.5 rounded-monumenta"
              onClick={() => { setCycleSheetTab('edit'); setCycleSheetOpen(true) }}
              data-testid="add-collaborator-btn"
            >
              <UserPlus className="h-4 w-4" /> Adicionar Colaborador
            </button>
          </div>
        )}

        {isEmpty && (
          <div className="text-center py-12" data-testid="empty-state">
            <Trophy className="mx-auto text-brand-gray/40 mb-4" size={48} />
            <p className="text-brand-gray text-lg mb-2">
              Nenhum ciclo de bonificacao configurado para {selectedYear}
            </p>
            <p className="text-brand-gray/60 text-sm mb-4">
              Configure o template e selecione os colaboradores para iniciar
            </p>
            {isAdmin && (
              <button
                type="button"
                className="btn-primary px-6 py-2.5 rounded-monumenta text-sm font-medium"
                onClick={() => { setCycleSheetTab('new'); setCycleSheetOpen(true) }}
                data-testid="setup-cycle-btn"
              >
                Configurar Ciclo {selectedYear}
              </button>
            )}
          </div>
        )}

        {/* Stats Grid — always rendered (shows zeros when empty) */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          data-testid="stats-grid"
        >
          {isLoading ? (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-monumenta" />
              ))}
            </>
          ) : (
            <>
              <div data-testid="stat-total">
                <MetricCard title="Total Avaliações" value={stats?.total ?? 0} icon={Users} />
              </div>
              <div data-testid="stat-rascunho">
                <MetricCard title="Rascunho" value={stats?.byStatus.rascunho ?? 0} icon={FileEdit} />
              </div>
              <div data-testid="stat-em_apuracao">
                <MetricCard title="Em Apuração" value={stats?.byStatus.em_apuracao ?? 0} icon={ClipboardCheck} />
              </div>
              <div data-testid="stat-validacao_rh">
                <MetricCard title="Validação RH" value={stats?.byStatus.validacao_rh ?? 0} icon={ShieldCheck} />
              </div>
              <div data-testid="stat-finalizado">
                <MetricCard title="Finalizado" value={stats?.byStatus.finalizado ?? 0} icon={CheckCircle} />
              </div>
              <div data-testid="stat-avg-bonus">
                <MetricCard
                  title="Média Bônus"
                  value={`${(stats?.avgBonus ?? 0).toFixed(2)} sal.`}
                  icon={TrendingUp}
                />
              </div>
            </>
          )}
        </div>

        {/* Collaborator Grid */}
        {showFilters && !isLoading && (
          <CollaboratorFilters assessments={visibleAssessments} />
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-48 w-full rounded-monumenta" />
            ))}
          </div>
        ) : (
          <>
            {!isEmpty && (
              <p className="text-sm text-brand-gray" data-testid="filter-count">
                {filteredAssessments.length} avaliação(ões)
              </p>
            )}

            {filteredAssessments.length === 0 ? (
              <div
                className="text-center py-12"
                data-testid="collaborator-grid-empty"
              >
                <p className="text-brand-gray">
                  {isEmpty
                    ? 'Nenhuma avaliação cadastrada para este ano'
                    : 'Nenhuma avaliação encontrada para os filtros selecionados'}
                </p>
                {!isEmpty && (
                  <button
                    type="button"
                    className="btn-ghost mt-4"
                    onClick={() =>
                      setFilters({ department: '', status: '', search: '' })
                    }
                    data-testid="clear-filters-btn"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAssessments.map((assessment, index) => (
                  <div
                    key={assessment.assessmentId}
                    className="motion-safe:animate-fade-in-up"
                    style={{ animationDelay: `${Math.min(index * 50, 500)}ms`, animationFillMode: 'both' }}
                  >
                    <CollaboratorCard
                      assessment={assessment}
                      isRH={isAdmin}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Cycle Setup Sheet */}
      <CycleSetupSheet
        open={cycleSheetOpen}
        onOpenChange={setCycleSheetOpen}
        year={selectedYear}
      />
    </div>
  )
}

const BonificacaoPageProtected = withPageProtection(BonificacaoPage, PAGES.PERFORMANCE_BONIFICACAO)

export const Route = createFileRoute('/_auth/performance/bonificacao/')({
  component: BonificacaoPageProtected,
  validateSearch: (search: Record<string, unknown>): BonificacaoSearch => ({
    employee: search.employee as string | undefined,
  }),
})
